import React, { useContext } from 'react';

import assetModalContext from '@components/asset/modal/tabs/edit/asset_modal_context';

import UppyUploader from '@components/common/uppy/main';
import FileStackUploader from '@components/common/filestack_uploader/main';

import {
  ActionTypes,
  WatermarkDispatch,
  WatermarkPayloadOperations
} from '@components/asset/modal/tabs/edit/EditTabTypes';
import { LinkObject } from '@api/v4/assets/assetTypes';
import { UppyFile } from '@components/common/uppy/UppyTypes';
import { FileStackOnUpload } from '@/components/common/filestack_uploader/FileStackTypes';


interface WatermarkScopedContext {
  state: {
    editState: {
      watermark: LinkObject;
    };
  };
  dispatch: WatermarkDispatch;
}

const WatermarkComponent = (): React.ReactElement => {
  const { state, dispatch }: WatermarkScopedContext = useContext(assetModalContext);
  const { watermark } = state.editState;

  const deleteAssetWatermark = (): void => {
    const payload = {
      operation: WatermarkPayloadOperations.Delete
    };
    dispatch({ type: ActionTypes.WatermarkAction, payload });
    Notify.create({
      title: t("Remove the watermark by restoring each attachment to a previous version."),
      body: t("Visit the Workflow tab, then select an attachment."),
      type: "success",
    });
  };

  const updateWatermarkUppy = (files: UppyFile[]): void => {
    if (files.length === 0) return;
    const payload = {
      operation: WatermarkPayloadOperations.Update,
      url: files[0].url,
      filename: files[0].name
    };
    dispatch({ type: ActionTypes.WatermarkAction, payload });
    Notify.create({
      title: t("Watermark will begin processing once changes are saved"),
      type: "success",
    });
  };

  const updateWatermarkFileStack = (files: FileStackOnUpload): void => {
    if (files.filesUploaded.length === 0) return;
    const payload = {
      operation: WatermarkPayloadOperations.Update,
      url: files.filesUploaded[0].url,
      filename: files.filesUploaded[0].filename
    };
    dispatch({ type: ActionTypes.WatermarkAction, payload });
    Notify.create({
      title: t("Watermark will begin processing once changes are saved"),
      type: "success",
    });
  };

  const onError = (): void => {
    Notify.create({ type: 'error', title: t("Error Uploading Files") });
  };

  const uploadAreaId = "watermark-trigger-watermark";

  const uploadProvider = (): React.ReactElement => (
    BFG.hasFeature('uppy_uploader_features') ? (
      <>
        <div
          id={uploadAreaId}
          className="uppy-asset-watermark"
        >
          {t("Click to upload")}
        </div>
        <UppyUploader
          button
          handleUpload={updateWatermarkUppy}
          restrictions={{ maxNumberOfFiles: 1, allowedFileTypes: ['image/*'] }}
          template="watermark"
          trigger={`#${uploadAreaId}`}
          uniqIdentifier="uppy-watermark-watermark"
        />
      </>
    ) : (
      <div
        id={uploadAreaId}
      >
        <FileStackUploader
          dropzoneContainerId={uploadAreaId}
          onError={onError}
          onSuccess={updateWatermarkFileStack}
          options={
            {
              accept: ["image/png"],
              maxFiles: 1
            }
          }
          type="area"
        />
      </div>
    )
  );

  return (
    <div className="asset-advanced__feature">
      <span>{t("Custom Watermark")}</span>
      <div className="watermark-upload">
        {
          (watermark.url && watermark.filename) ? (
            <div className="asset-advanced__feature--upload-complete">
              <a
                className="asset-advanced__feature--link filename"
                href={watermark.url}
                rel="noopener noreferrer"
                target="_blank"
              >
                {watermark.filename}
              </a>
              <span
                role="button"
                tabIndex={0}
                className="bff-trash remove-watermark"
                title={t("Remove Custom Asset Watermark")}
                onClick={(): void => deleteAssetWatermark()}
                onKeyPress={(): void => deleteAssetWatermark()}
              />
            </div>
          ) : uploadProvider()
        }
      </div>
    </div>
  );
};

export default WatermarkComponent;
